import { type ElementType, type ReactNode } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { mergeSx } from "ui/merge-sx";
import { Typography } from "ui/typography";
import RouterLink from "components/partials/router-link/router-link";
import { StatusDisplay } from "components/partials/status-display/status-display";

import { Route } from "utilities/app-routes";

import { shadows } from "design-tokens";

/** Chakra `rounded="lg"` radius. */
const CARD_BORDER_RADIUS = "0.5rem";

interface CardSectionProps {
  component?: ElementType;
  className?: string;
  children?: ReactNode;
  title?: string;
  sx?: SxProps<Theme>;
}

export interface CardProps extends CardSectionProps {
  dark?: boolean;
}
const Card = ({
  dark = false,
  component = "div",
  className,
  children,
  title,
  sx,
}: CardProps) => (
  <Box
    component={component}
    data-testid="card"
    className={className}
    title={title}
    sx={mergeSx(
      {
        width: "100%",
        borderRadius: CARD_BORDER_RADIUS,
        boxShadow: shadows.md,
        backgroundColor: dark ? "grey.200" : "grey.50",
        fontSize: "0.875rem",
      },
      sx,
    )}>
    {children}
  </Box>
);

export const CardShowcase = ({
  component = "div",
  className,
  children,
  title,
  sx,
}: CardSectionProps) => (
  <Box
    component={component}
    data-testid="card-showcase"
    className={className}
    title={title}
    sx={mergeSx(
      {
        display: "flex",
        borderTopLeftRadius: CARD_BORDER_RADIUS,
        borderTopRightRadius: CARD_BORDER_RADIUS,
      },
      sx,
    )}>
    {children}
  </Box>
);

interface CardHeaderProps extends CardSectionProps {
  heading: string;
  eyebrow?: string;
  subscript?: string;
  status?: string;
  centered?: boolean;
  editLink?: string;
  statusLiveText?: string;
  statusArchivedText?: string;
  statusDraftText?: string;
  statusArchivedDraftText?: string;
  statusCreatedText?: string;
  statusClientReviewText?: string;
  statusApprovedText?: string;
  statusRejectedText?: string;
  statusSystemDraftText?: string;
}
export const CardHeader = ({
  heading,
  eyebrow,
  subscript,
  status,
  centered,
  editLink,
  children,
  statusLiveText,
  statusArchivedText,
  statusDraftText,
  statusArchivedDraftText,
  statusCreatedText,
  statusClientReviewText,
  statusApprovedText,
  statusRejectedText,
  statusSystemDraftText,
  component = "div",
  className,
  sx,
}: CardHeaderProps) => (
  <Box
    component={component}
    data-testid="card-header"
    className={className}
    sx={mergeSx(
      {
        display: "flex",
        justifyContent: centered ? "center" : "space-between",
        p: 2,
      },
      sx,
    )}>
    <Box sx={{ display: "flex", overflow: "hidden", flexDirection: "column" }}>
      {eyebrow && (
        <Typography
          variant="h5"
          data-testid="eyebrow"
          sx={{ color: "text.secondary", mb: 0.5 }}>
          {eyebrow}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Typography
          variant="h4"
          sx={{
            color: "secondary.main",
            mr: 1,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}>
          {heading}
        </Typography>
        {subscript && (
          <Typography
            variant="body2"
            component="small"
            data-testid="subscript"
            sx={{ color: "text.secondary", fontWeight: 300 }}>
            {subscript}
          </Typography>
        )}
      </Box>
    </Box>
    {status && (
      <StatusDisplay
        status={status}
        liveText={statusLiveText}
        archivedText={statusArchivedText}
        draftText={statusDraftText}
        archivedDraftText={statusArchivedDraftText}
        createdText={statusCreatedText}
        clientReviewText={statusClientReviewText}
        approvedText={statusApprovedText}
        rejectedText={statusRejectedText}
        systemDraftText={statusSystemDraftText}
      />
    )}

    {editLink && (
      <RouterLink sx={{ fontWeight: 700 }} to={editLink || Route.notFound}>
        Edit
      </RouterLink>
    )}

    {children}
  </Box>
);

export const CardBody = ({
  component = "div",
  className,
  children,
  sx,
}: CardSectionProps) => (
  <Box
    component={component}
    data-testid="card-body"
    className={className}
    sx={mergeSx({ p: 2 }, sx)}>
    {children}
  </Box>
);

export interface CardRowProps extends CardSectionProps {
  title: string;
  value?: ReactNode;
}
export const CardRow = ({ title, value, component = "div", className, sx }: CardRowProps) => (
  <Box
    component={component}
    data-testid="card-row"
    className={className}
    sx={mergeSx(
      { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 },
      sx,
    )}>
    <Typography variant="inherit" component="span" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    {Array.isArray(value) ? (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "end" }}>
        {value.map((v, ind) => (
          <Typography variant="inherit" component="span" key={ind} data-testid={`${title}-value`}>
            {v || "undefined"}
          </Typography>
        ))}
      </Box>
    ) : (
      <Typography variant="inherit" component="span" data-testid={`${title}-value`}>
        {value || "None"}
      </Typography>
    )}
  </Box>
);

export default Card;
